import { Nullable } from "@core/domain/types/nullable.type";
import { convertEmptyToUndefined } from "@core/domain/types/undefinable.type";
import { CreateTechnicalProposalIndicatorBody } from "@project/data/dto/technical-proposal/create-technical-proposal.body";
import { EditTechnicalProposalIndicatorBody } from "@project/data/dto/technical-proposal/edit-technical-proposal.body";
import {
    CreateTechnicalProposalIndicatorCommentBody,
    TechnicalProposalIndicatorCommentDto,
    TechnicalProposalIndicatorDto,
} from "@project/data/dto/technical-proposal/technical-proposal.dto";
import {
    CreateIndicator,
    CreateIndicatorComment,
    EditIndicator,
    Indicator,
    IndicatorComment,
    IndicatorCommentTypeEnum,
    IndicatorDerivedStatusCodeEnum,
    IndicatorFrequencyEnum,
    IndicatorPurposeTypologyEnum,
    IndicatorTargetConfig,
    IndicatorTargetTypeEnum,
    IndicatorTypeEnum,
} from "@project/domain/models/technical-proposals.model";
import { IndicatorFormValuesValidated } from "@project/presentation/components/project-form/technical-proposal/indicator/indicator-form.view";
import { validateSync } from "class-validator";
import { injectable } from "inversify";
import { DateTime } from "luxon";

@injectable()
export class IndicatorMapper {
    private deserializeTargetConfig(
        targetConfig?: Record<string, unknown>,
    ): IndicatorTargetConfig | undefined {
        if (!targetConfig || typeof targetConfig !== "object") {
            return undefined;
        }

        const rawTrafficLight = (targetConfig.trafficLight ??
            targetConfig.traffic_light) as Record<string, unknown> | undefined;
        const rawCategory = (targetConfig.category ?? null) as Record<
            string,
            unknown
        > | null;

        const normalizedTrafficLight = rawTrafficLight
            ? {
                  greenLabel: (rawTrafficLight.greenLabel ??
                      rawTrafficLight.green_label) as string | undefined,
                  yellowLabel: (rawTrafficLight.yellowLabel ??
                      rawTrafficLight.yellow_label) as string | undefined,
                  redLabel: (rawTrafficLight.redLabel ??
                      rawTrafficLight.red_label) as string | undefined,
              }
            : undefined;

        const normalizedCategory = rawCategory
            ? {
                  options: Array.isArray(rawCategory.options)
                      ? rawCategory.options.filter(
                            (option): option is string =>
                                typeof option === "string",
                        )
                      : undefined,
              }
            : undefined;

        if (!normalizedTrafficLight && !normalizedCategory) {
            return undefined;
        }

        return {
            trafficLight: normalizedTrafficLight,
            category: normalizedCategory,
        };
    }

    private serializeTargetConfig(
        targetConfig?: IndicatorTargetConfig,
    ): Record<string, unknown> | undefined {
        if (!targetConfig) {
            return undefined;
        }

        const trafficLight = targetConfig.trafficLight
            ? {
                  green_label: targetConfig.trafficLight.greenLabel,
                  yellow_label: targetConfig.trafficLight.yellowLabel,
                  red_label: targetConfig.trafficLight.redLabel,
              }
            : undefined;

        const category = targetConfig.category?.options?.length
            ? {
                  options: targetConfig.category.options,
              }
            : undefined;

        if (!trafficLight && !category) {
            return undefined;
        }

        return {
            traffic_light: trafficLight,
            category,
        };
    }

    private buildTargetConfig(
        indicatorForm: IndicatorFormValuesValidated,
    ): IndicatorTargetConfig | undefined {
        if (indicatorForm.targetType === IndicatorTargetTypeEnum.trafficLight) {
            const greenLabel = indicatorForm.trafficLightGreenLabel.trim();
            const yellowLabel = indicatorForm.trafficLightYellowLabel.trim();
            const redLabel = indicatorForm.trafficLightRedLabel.trim();

            if (!greenLabel && !yellowLabel && !redLabel) {
                return undefined;
            }

            return {
                trafficLight: {
                    greenLabel,
                    yellowLabel,
                    redLabel,
                },
            };
        }

        if (indicatorForm.targetType === IndicatorTargetTypeEnum.category) {
            const options = indicatorForm.categoryOptionsText
                .split(/\r?\n|,|;/)
                .map((option) => option.trim())
                .filter(Boolean);

            if (!options.length) {
                return undefined;
            }

            return {
                category: {
                    options,
                },
            };
        }

        return undefined;
    }

    map(indicatorDto: TechnicalProposalIndicatorDto): Nullable<Indicator> {
        const errors = validateSync(indicatorDto);

        if (Boolean(errors.length)) {
            console.error(errors);

            return null;
        }

        const indicator = new Indicator(
            indicatorDto.id,
            indicatorDto.activity,
            indicatorDto.name ?? "",
            indicatorDto.description ?? "",
            indicatorDto.calculation_criteria ?? "",
            indicatorDto.estimated_indicator,
            indicatorDto.achieved_indicator,
            indicatorDto.verification_source,
            (indicatorDto.frequency as IndicatorFrequencyEnum) ??
                IndicatorFrequencyEnum.monthly,
            (indicatorDto.indicator_type as IndicatorTypeEnum) ??
                IndicatorTypeEnum.quantitative,
            (indicatorDto.target_type as IndicatorTargetTypeEnum) ??
                IndicatorTargetTypeEnum.absolute,
            (indicatorDto.purpose_typology as IndicatorPurposeTypologyEnum) ??
                null,
            indicatorDto.responsible_employee ?? undefined,
            indicatorDto.status ?? "",
            indicatorDto.activity_name,
            indicatorDto.disaggregate_by_gender ?? false,
            {
                women: indicatorDto.estimated_gender_breakdown?.women,
                men: indicatorDto.estimated_gender_breakdown?.men,
                nonBinary: indicatorDto.estimated_gender_breakdown?.non_binary,
                unspecified:
                    indicatorDto.estimated_gender_breakdown?.unspecified,
            },
            {
                women: indicatorDto.achieved_gender_breakdown?.women,
                men: indicatorDto.achieved_gender_breakdown?.men,
                nonBinary: indicatorDto.achieved_gender_breakdown?.non_binary,
                unspecified:
                    indicatorDto.achieved_gender_breakdown?.unspecified,
            },
        );

        indicator.targetConfig = this.deserializeTargetConfig(
            indicatorDto.target_config,
        );

        indicator.frequencyLabel = indicatorDto.frequency_label;
        indicator.indicatorTypeLabel = indicatorDto.indicator_type_label;
        indicator.purposeTypologyLabel = indicatorDto.purpose_typology_label;
        indicator.targetTypeLabel = indicatorDto.target_type_label;
        indicator.responsibleEmployeeName =
            indicatorDto.responsible_employee_name;
        indicator.commentCount = indicatorDto.comment_count;
        indicator.latestCommentText = indicatorDto.latest_comment_text;
        indicator.lastMonitoringDate = indicatorDto.last_monitoring_date
            ? DateTime.fromISO(indicatorDto.last_monitoring_date)
            : null;
        indicator.nextMonitoringDueDate = indicatorDto.next_monitoring_due_date
            ? DateTime.fromISO(indicatorDto.next_monitoring_due_date)
            : null;
        indicator.monitoringUpdateOverdue =
            indicatorDto.monitoring_update_overdue;
        indicator.completionPercentage = indicatorDto.completion_percentage;
        indicator.derivedStatus = indicatorDto.derived_status;
        indicator.derivedStatusCode =
            (indicatorDto.derived_status_code as IndicatorDerivedStatusCodeEnum) ??
            undefined;

        if (indicatorDto.activity_slots) {
            indicator.slots = indicatorDto.activity_slots;
        }
        if (indicatorDto.tasks_data) {
            indicator.tasks = indicatorDto.tasks_data.map((task) => ({
                id: task.id,
                description: task.description,
            }));
        }

        return indicator;
    }

    mapComment(
        indicatorCommentDto: TechnicalProposalIndicatorCommentDto,
    ): Nullable<IndicatorComment> {
        const errors = validateSync(indicatorCommentDto);

        if (Boolean(errors.length)) {
            console.error(errors);

            return null;
        }

        return new IndicatorComment(
            indicatorCommentDto.id,
            indicatorCommentDto.indicator,
            indicatorCommentDto.text,
            indicatorCommentDto.comment_type as IndicatorCommentTypeEnum,
            DateTime.fromISO(indicatorCommentDto.created),
            DateTime.fromISO(indicatorCommentDto.modified),
            indicatorCommentDto.created_by_name,
            indicatorCommentDto.reference_date
                ? DateTime.fromISO(indicatorCommentDto.reference_date)
                : null,
            indicatorCommentDto.comment_type_label,
        );
    }

    mapFromFormToCreate(
        indicatorForm: IndicatorFormValuesValidated,
    ): CreateIndicator {
        if (!indicatorForm.activityId)
            throw new Error("activityId is required");

        return new CreateIndicator(
            indicatorForm.activityId,
            indicatorForm.name,
            indicatorForm.description,
            indicatorForm.calculationCriteria,
            indicatorForm.estimatedIndicator,
            indicatorForm.achievedIndicator
                ? indicatorForm.achievedIndicator
                : null,
            indicatorForm.verificationSource,
            indicatorForm.frequency,
            indicatorForm.indicatorType,
            indicatorForm.targetType,
            indicatorForm.purposeTypology ?? null,
            indicatorForm.disaggregateByGender,
            {
                women: indicatorForm.estimatedWomen,
                men: indicatorForm.estimatedMen,
                nonBinary: indicatorForm.estimatedNonBinary,
                unspecified: indicatorForm.estimatedUnspecified,
            },
            {
                women: indicatorForm.achievedWomen,
                men: indicatorForm.achievedMen,
                nonBinary: indicatorForm.achievedNonBinary,
                unspecified: indicatorForm.achievedUnspecified,
            },
            this.buildTargetConfig(indicatorForm),
            indicatorForm.responsibleEmployeeId ?? null,
        );
    }

    mapFromFormToEdit(
        indicatorForm: IndicatorFormValuesValidated,
    ): EditIndicator {
        if (!indicatorForm.id) throw new Error("Indicator id is required");
        if (!indicatorForm.activityId)
            throw new Error("activityId is required");

        const indicator = new EditIndicator(
            indicatorForm.id,
            indicatorForm.activityId,
            indicatorForm.name,
            indicatorForm.description,
            indicatorForm.calculationCriteria,
            indicatorForm.estimatedIndicator,
            indicatorForm.achievedIndicator
                ? indicatorForm.achievedIndicator
                : null,
            indicatorForm.verificationSource,
            indicatorForm.frequency,
            indicatorForm.indicatorType,
            indicatorForm.targetType,
            indicatorForm.purposeTypology ?? null,
            indicatorForm.disaggregateByGender,
            {
                women: indicatorForm.estimatedWomen,
                men: indicatorForm.estimatedMen,
                nonBinary: indicatorForm.estimatedNonBinary,
                unspecified: indicatorForm.estimatedUnspecified,
            },
            {
                women: indicatorForm.achievedWomen,
                men: indicatorForm.achievedMen,
                nonBinary: indicatorForm.achievedNonBinary,
                unspecified: indicatorForm.achievedUnspecified,
            },
            this.buildTargetConfig(indicatorForm),
            indicatorForm.responsibleEmployeeId ?? null,
        );

        return indicator;
    }

    mapToCreateDto(
        indicator: CreateIndicator,
    ): CreateTechnicalProposalIndicatorBody {
        const responsibleEmployee: Nullable<number> =
            indicator.responsibleEmployeeId ?? null;

        return {
            activity: indicator.activityId,
            name: indicator.name,
            description: indicator.description,
            calculation_criteria: indicator.calculationCriteria,
            estimated_indicator: indicator.estimatedIndicator,
            achieved_indicator: indicator.achievedIndicator,
            verification_source: indicator.verificationFont,
            frequency: indicator.frequency,
            indicator_type: indicator.indicatorType,
            purpose_typology: indicator.purposeTypology ?? null,
            target_type: indicator.targetType,
            disaggregate_by_gender: indicator.disaggregateByGender,
            estimated_gender_breakdown: {
                women: indicator.estimatedGenderBreakdown.women ?? null,
                men: indicator.estimatedGenderBreakdown.men ?? null,
                non_binary:
                    indicator.estimatedGenderBreakdown.nonBinary ?? null,
                unspecified:
                    indicator.estimatedGenderBreakdown.unspecified ?? null,
            },
            achieved_gender_breakdown: {
                women: indicator.achievedGenderBreakdown.women ?? null,
                men: indicator.achievedGenderBreakdown.men ?? null,
                non_binary: indicator.achievedGenderBreakdown.nonBinary ?? null,
                unspecified:
                    indicator.achievedGenderBreakdown.unspecified ?? null,
            },
            target_config: this.serializeTargetConfig(indicator.targetConfig),
            responsible_employee: responsibleEmployee,
        };
    }

    mapToEditDto(indicator: EditIndicator): EditTechnicalProposalIndicatorBody {
        const responsibleEmployee: Nullable<number> =
            indicator.responsibleEmployeeId ?? null;

        return {
            id: indicator.id,
            activity: indicator.activityId,
            name: indicator.name,
            description: indicator.description,
            calculation_criteria: indicator.calculationCriteria,
            estimated_indicator: indicator.estimatedIndicator,
            achieved_indicator: indicator.achievedIndicator,
            verification_source: indicator.verificationFont,
            frequency: indicator.frequency,
            indicator_type: indicator.indicatorType,
            purpose_typology: indicator.purposeTypology ?? null,
            target_type: indicator.targetType,
            disaggregate_by_gender: indicator.disaggregateByGender,
            estimated_gender_breakdown: {
                women: indicator.estimatedGenderBreakdown.women ?? null,
                men: indicator.estimatedGenderBreakdown.men ?? null,
                non_binary:
                    indicator.estimatedGenderBreakdown.nonBinary ?? null,
                unspecified:
                    indicator.estimatedGenderBreakdown.unspecified ?? null,
            },
            achieved_gender_breakdown: {
                women: indicator.achievedGenderBreakdown.women ?? null,
                men: indicator.achievedGenderBreakdown.men ?? null,
                non_binary: indicator.achievedGenderBreakdown.nonBinary ?? null,
                unspecified:
                    indicator.achievedGenderBreakdown.unspecified ?? null,
            },
            target_config: this.serializeTargetConfig(indicator.targetConfig),
            responsible_employee: responsibleEmployee,
            activity_slots: convertEmptyToUndefined(indicator.slots),
            status: indicator.status ?? "",
        };
    }

    mapCommentToCreateDto(
        comment: CreateIndicatorComment,
    ): CreateTechnicalProposalIndicatorCommentBody {
        return {
            text: comment.text,
            comment_type: comment.commentType,
            reference_date: comment.referenceDate
                ? comment.referenceDate.toISODate()
                : null,
        };
    }
}
